import { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import CrossIcon from '@stokr/components-library/dist/images/cross-icon.svg?react'
import { Button, Copy, Modal, ModalBox, t } from '@stokr/components-library'
import { ExternalLinkIcon } from 'components/ui/icons'
import { RoundButton } from 'components/sto/Documents/Documents.styles'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'
import { Notice } from './Notice'
import { useStoTrack } from 'hooks/sto/useStoTracking'

/** Above the mobile KeyActions sheet (1002) it opens from. */
const RiskModal = styled(Modal)`
  z-index: 1003;

  ${ModalBox} {
    max-width: 762px !important;
    border-radius: ${stoRadius.lg};
    font-size: inherit;
  }

  /* Figma 3410:69051: full screen on mobile. Pinned to the screen and scrolling itself, so a
     long text or the browser bar resizing never scrolls the page behind into view. */
  ${stoMedia.belowDesktop} {
    ${ModalBox} {
      position: fixed;
      inset: 0;
      display: flex;
      flex-direction: column;
      max-width: none !important;
      overflow-y: auto;
      overscroll-behavior: contain;
      border-radius: 0;
    }
  }
`

const Inner = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${stoSpace[4]};
  padding: ${stoSpace[6]};

  ${stoMedia.belowDesktop} {
    gap: ${stoSpace[5]};
  }
`

const Close = styled(RoundButton)`
  align-self: flex-end;

  ${stoMedia.belowDesktop} {
    width: 48px;
    height: 48px;
    padding: 14px;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${stoSpace[10]};
  padding: 0 ${stoSpace[12]} ${stoSpace[12]};
  text-align: center;

  ${stoMedia.belowDesktop} {
    flex: 1;
    justify-content: center;
    gap: 80px;
    padding: 0 0 ${stoSpace[12]};
  }
`

const Copyblock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
  width: 100%;
  text-align: center;

  /* The notice reads left-aligned, as in Figma. */
  & > :last-child {
    text-align: left;
  }
`

const Title = styled.h2`
  ${stoType.headingMd};
  margin: 0;
  color: ${stoColor.textPrimary};
`

const Text = styled.p`
  ${stoType.bodyLg};
  margin: 0;
  font-weight: 300;
  color: ${stoColor.textPrimary};
`

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${stoSpace[4]};

  ${stoMedia.belowDesktop} {
    flex-direction: column;
    width: 100%;
  }
`

/** Library primary / text `Button`s at Figma's large size (label/cta). */
const Action = styled(Button)`
  ${stoType.labelCta};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${stoSpace[2]};
  /* Figma 48px tall: 14px padding, less the library 1px border. */
  padding: 13px 32px;
  color: ${({ onlyText }) => (onlyText ? stoColor.textSecondary : stoColor.buttonLabelOnFill)} !important;
`

interface TradeRiskModalProps {
  isOpen: boolean
  venue: string
  url: string
  /** Keep it stable (useCallback): the focus and Escape handling re-binds when it changes. */
  onClose: () => void
}

/** Figma 3390:38495: the risk acknowledgement before leaving STOKR for an external venue. */
export const TradeRiskModal = ({ isOpen, venue, url, onClose }: TradeRiskModalProps) => {
  // The library Modal does neither: focus moves to the close button as it mounts and back to
  // the trigger on close, and Escape closes it. Captured, so the mobile sheet never sees it.
  const focusOnMount = useCallback((el: HTMLButtonElement | null) => el?.focus({ preventScroll: true }), [])
  const trackSto = useStoTrack()
  const dismiss = useCallback(() => {
    trackSto('sto_trade_risk_dismissed', { venue })
    onClose()
  }, [trackSto, venue, onClose])

  useEffect(() => {
    if (!isOpen) return undefined
    const trigger = document.activeElement as HTMLElement | null
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      dismiss()
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      trigger?.focus({ preventScroll: true })
    }
  }, [isOpen, dismiss])

  const proceed = () => {
    trackSto('sto_trade_risk_accepted', { venue })
    window.open(url, '_blank', 'noopener,noreferrer')
    onClose()
  }

  return (
    <RiskModal isOpen={isOpen} onClose={dismiss} hideCloseButton>
      <Inner role="dialog" aria-modal="true" aria-label={t('stoPage.keyActions.trade.riskModal.title', { venue })}>
        <Close
          ref={focusOnMount}
          $size={40}
          type="button"
          aria-label={t('common.close')}
          data-cy="sto-trade-risk-modal-close"
          onClick={dismiss}>
          <CrossIcon width={16} height={16} aria-hidden="true" />
        </Close>
        <Content>
          <Copyblock>
            <Title>
              <Copy id="stoPage.keyActions.trade.riskModal.title" values={{ venue }} />
            </Title>
            <Text>
              <Copy id="stoPage.keyActions.trade.disclaimer" />
            </Text>
            <Notice
              variant="warning"
              titleKey="stoPage.keyActions.trade.riskModal.riskTitle"
              textKey="stoPage.keyActions.trade.riskModal.riskText"
              values={{ venue }}
            />
          </Copyblock>
          <Actions>
            <Action type="button" data-cy="sto-trade-risk-modal-proceed" onClick={proceed}>
              <Copy id="stoPage.keyActions.trade.riskModal.proceed" values={{ venue }} />
              <ExternalLinkIcon width={20} height={20} aria-hidden="true" />
            </Action>
            <Action type="button" onlyText data-cy="sto-trade-risk-modal-back" onClick={dismiss}>
              <Copy id="stoPage.keyActions.trade.riskModal.back" />
            </Action>
          </Actions>
        </Content>
      </Inner>
    </RiskModal>
  )
}
